import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { supabase } from "../lib/supabase"

interface Props {
  language: "tr" | "en"
  onLogout: () => void
  onLanguageChange: (l: "tr" | "en") => void
}

export const Paywall = ({ language, onLogout, onLanguageChange }: Props) => {
  const [step, setStep] = useState<"wall" | "done" | "error">("wall")
  const [loading, setLoading] = useState(false)

  const t = language === "tr" ? {
    headline: "Her gün bir şeyi seçiyorsun.",
    sub: "Yapacağın. Ve görmezden gelemeyeceğin.",
    value1: "Dün ortada.",
    value2: "Bugün seçiyorsun.",
    value3: "Sonuç kaçmıyor.",
    break: "Kaçamıyorsun.",
    cta: "Erişim İste",
    trust: "Erken erişim sınırlı.",
    logout: "Çıkış yap",
    doneTitle: "Listeye eklendin.",
    doneSub: "Yer açıldığında haber vereceğiz.",
    contact: "Sorularınız için: blindspot@blindspothq.com",
    errorTitle: "Bir şeyler ters gitti.",
    errorSub: "Lütfen tekrar dene.",
    retry: "Tekrar dene",
  } : {
    headline: "Every day, you choose one thing.",
    sub: "Something you'll do. And won't ignore.",
    value1: "Yesterday is clear.",
    value2: "Today, you choose.",
    value3: "The outcome stays.",
    break: "You don't ignore it.",
    cta: "Request Access",
    trust: "Early access is limited.",
    logout: "Log out",
    doneTitle: "You're on the list.",
    doneSub: "We'll let you know when it opens.",
    contact: "Questions? blindspot@blindspothq.com",
    errorTitle: "Something went wrong.",
    errorSub: "Please try again.",
    retry: "Try again",
  }

  const handleRequest = async () => {
    setLoading(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { setStep("error"); return }

      const { error } = await supabase.from("access_requests").insert({
        user_id: user.id,
        email: user.email,
      })

      // 23505 = unique violation: zaten listede, yine de "done" göster
      if (error && error.code !== "23505") {
        console.error("Access request failed:", error)
        setStep("error")
        return
      }

      setStep("done")
    } catch (err) {
      console.error("Access request error:", err)
      setStep("error")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="app">
      <div className="header">
        <h1>DECISION ZERO</h1>
        <div className="lang">
          <button onClick={() => onLanguageChange("en")} disabled={language === "en"}>EN</button>
          <button onClick={() => onLanguageChange("tr")} disabled={language === "tr"}>TR</button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {step === "wall" && (
          <motion.div
            key="wall"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="step-container"
            style={{ flex: "none", textAlign: "center" }}
          >
            <div style={{ fontSize: "22px", fontWeight: "600", color: "var(--text-primary)", lineHeight: "1.5", marginBottom: "8px" }}>
              {t.headline}
            </div>
            <div style={{ fontSize: "15px", color: "var(--text-secondary)", marginBottom: "28px" }}>
              {t.sub}
            </div>
            <div style={{ marginBottom: "28px", textAlign: "left" }}>
              {[t.value1, t.value2, t.value3].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                  <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "var(--accent)", flexShrink: 0 }} />
                  <div style={{ fontSize: "15px", color: "var(--text-primary)" }}>{item}</div>
                </div>
              ))}
            </div>
            <div style={{
              padding: "20px",
              background: "rgba(123,92,255,0.06)",
              border: "1px solid rgba(123,92,255,0.2)",
              borderRadius: "14px",
              marginBottom: "32px",
              fontSize: "16px",
              fontWeight: "500",
              color: "var(--text-primary)",
            }}>
              {t.break}
            </div>
            <button onClick={handleRequest} disabled={loading} className="btn-primary" style={{ opacity: loading ? 0.4 : 1 }}>
              {loading ? "..." : t.cta}
            </button>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "12px" }}>
              {t.trust}
            </div>
            <button onClick={onLogout} className="btn-secondary" style={{ marginTop: "16px" }}>
              {t.logout}
            </button>
          </motion.div>
        )}

        {step === "done" && (
          <motion.div
            key="done"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="step-container"
            style={{ flex: "none", textAlign: "center" }}
          >
            <div style={{ fontSize: "20px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "12px" }}>
              {t.doneTitle}
            </div>
            <div style={{ fontSize: "15px", color: "var(--text-secondary)", marginBottom: "20px" }}>
              {t.doneSub}
            </div>
            <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>
              {t.contact}
            </div>
          </motion.div>
        )}

        {step === "error" && (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="step-container"
            style={{ flex: "none", textAlign: "center" }}
          >
            <div style={{ fontSize: "20px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "12px" }}>
              {t.errorTitle}
            </div>
            <div style={{ fontSize: "15px", color: "var(--text-secondary)", marginBottom: "20px" }}>
              {t.errorSub}
            </div>
            <button onClick={() => setStep("wall")} className="btn-primary">
              {t.retry}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}